import { useMemo, useState } from "react";
import moment from "moment";
import { ExpandableTable } from "../../../components/UniversalTable";
import { defaultTableColumns } from "../../../helpers";
import { BankItem } from "../../../types/verified/bank";
import { toMoney } from "shared/utils";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { NameMismatchAlert } from "./NameMismatchAlert";
import { Tabs } from "shared/components/BaseTabs";
import { IncomeSource } from "shared/types";

type BankStreamItem = {
  id: number;
  income_id: number | null;
  data: BankItem["data"];
};

type BankSourceRow = {
  source: string;
  dateRange: string;
  grossMonthly: string;
  netMonthly: string;
  original: BankStreamItem;
};

const formatDateRange = (start?: string, end?: string | null) =>
  (start ? moment(start).format("ll") : "-") +
  " - " +
  (end ? moment(end).format("ll") : "Present");

const buildRow = (item: BankStreamItem): BankSourceRow => ({
  source:
    item.data.institution_metadata?.name || item.data.employer?.name || "",
  dateRange: formatDateRange(item.data.start_date, item.data.end_date),
  grossMonthly:
    item.data.gross_monthly_income_amount != null
      ? (toMoney(item.data.gross_monthly_income_amount) ?? "-")
      : "-",
  netMonthly: toMoney(item.data.net_monthly_income_amount) ?? "-",
  original: item
});

const normalizeBankItem = (item: BankItem): BankStreamItem => ({
  id: item.id,
  income_id: item.income_id,
  data: item.data
});

const normalizeUnassignedItem = (
  item: IncomeSource["items"][number]
): BankStreamItem => ({
  id: item.id,
  income_id: item.income_id,
  data: item.data as unknown as BankItem["data"]
});

const BankStreamExpanded = ({ item }: { item: BankStreamItem }) => {
  const nameMismatch = item.data.name_mismatch;
  return (
    <div className="px-6 py-8 bg-background-900">
      {nameMismatch ? (
        <NameMismatchAlert nameMismatch={nameMismatch} className="mb-4" />
      ) : null}
      <ContentTitle title="Income details" contentClassName="space-y-2">
        <KeyValue label="Employer name" value={item.data.employer?.name} />
        <KeyValue
          label="Income start date"
          value={
            item.data.start_date
              ? moment(item.data.start_date).format("ll")
              : "-"
          }
        />
        <KeyValue
          label="Most recent pay date"
          value={
            item.data.end_date ? moment(item.data.end_date).format("ll") : "-"
          }
        />
        <KeyValue
          label="Pay frequency"
          value={item.data.pay_frequency || "-"}
        />
        <KeyValue
          label="Transaction count"
          value={item.data.transaction_count?.toString()}
        />
        <KeyValue
          label="Monthly net income"
          value={toMoney(item.data.net_monthly_income_amount)}
        />
        <KeyValue
          label="Monthly gross income"
          value={toMoney(item.data.gross_monthly_income_amount)}
        />
        <KeyValue
          label="Forecasted monthly income"
          value={toMoney(item.data.forecasted_monthly_income_amount)}
        />
      </ContentTitle>
    </div>
  );
};

type BankSourcesTab = "Selected" | "Other";

const tabItems = [
  { value: "Selected" as BankSourcesTab, label: "Selected income sources" },
  { value: "Other" as BankSourcesTab, label: "Other income sources" }
];

export const BankSourcesTable = ({
  income,
  plaidBankSource
}: {
  income: BankItem[];
  plaidBankSource?: IncomeSource | null;
}) => {
  const currentIncomeId = income[0]?.income_id;

  const assignedRows = useMemo(
    () => income.map((item) => buildRow(normalizeBankItem(item))),
    [income]
  );

  const unassignedRows = useMemo(() => {
    if (!plaidBankSource || currentIncomeId == null) return [];
    return plaidBankSource.items
      .filter((item) => item.income_id !== currentIncomeId)
      .map((item) => buildRow(normalizeUnassignedItem(item)));
  }, [plaidBankSource, currentIncomeId]);

  const hasOtherSources = unassignedRows.length > 0;

  const [activeTab, setActiveTab] = useState<BankSourcesTab>("Selected");

  const rows = activeTab === "Other" ? unassignedRows : assignedRows;

  return (
    <>
      {hasOtherSources ? (
        <div className="mb-4 mt-6 border-b border-alpha-80">
          <Tabs
            tabs={tabItems}
            active={activeTab}
            onChange={(t) => setActiveTab(t)}
            tabStyle="line"
            size="sm"
            className="w-fit"
          />
        </div>
      ) : null}

      <ExpandableTable
        key={activeTab}
        cellClassName="p-4 pl-0 text-sm self-center"
        headerRowClassName="text-content-300 !font-medium"
        renderExpanded={({ original }) => (
          <BankStreamExpanded item={original} />
        )}
        rows={rows}
        columns={defaultTableColumns}
        hideExpandAll
      />
    </>
  );
};
